import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Competition } from '@/types/domain';
import type { SportFilterValue } from '@/components/shared/SportFilter';
import CompetitionGameList from './CompetitionGameList';
import { filterCompetitionsBySport } from './filterCompetitionsBySport';

export interface RecentTabProps {
  competitions: Competition[];
  selectedSport: SportFilterValue;
  isLoading: boolean;
  showBodyAds: boolean;
  onGameAction: (gameId: string) => void;
  onViewLeaderboard: (gameId: string) => void;
  onMenuClick: (gameId: string) => void;
}

/**
 * Recent tab — completed games within the last 7 days.
 * Empty state mirrors My Picks (Figma 10203:62430 pattern).
 */
const RecentTab: React.FC<RecentTabProps> = ({
  competitions,
  selectedSport,
  isLoading,
  showBodyAds,
  onGameAction,
  onViewLeaderboard,
  onMenuClick,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const games = useMemo(() => {
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    return filterCompetitionsBySport(competitions, selectedSport).filter(
      g => g.state === 'final' && g.gameDate >= sevenDaysAgo
    );
  }, [competitions, selectedSport]);

  return (
    <CompetitionGameList
      games={games}
      isLoading={isLoading}
      skeletonCount={3}
      emptyTitle={t('pages.noRecentResults')}
      emptySubtitle={t('pages.noRecentResultsSubtitle')}
      emptyActionLabel={t('pages.viewAvailableContests')}
      onEmptyAction={() => navigate('/main/lobby')}
      showEmptyIcon
      showBodyAds={showBodyAds}
      variant="recent"
      onGameAction={onGameAction}
      onViewLeaderboard={onViewLeaderboard}
      onMenuClick={onMenuClick}
    />
  );
};

export default RecentTab;
